import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Loader2,
  Users,
  Building2,
  User,
  Phone,
  Briefcase,
  ChevronRight,
  Receipt,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import Pagination from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { useAuth, hasRole } from "@/contexts/AuthContext";

export default function CustomersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const canCreate = hasRole(user, "admin", "sales");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [customerType, setCustomerType] = useState(searchParams.get("customer_type") || "all");
  const [isActive, setIsActive] = useState(searchParams.get("is_active") || "true");

  const page = parseInt(searchParams.get("page") || "1", 10);
  const pageSize = parseInt(searchParams.get("page_size") || "25", 10);

  // New Customer Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newCust, setNewCust] = useState({
    customer_type: "individual",
    display_name: "",
    company_name: "",
    phone: "",
    alternative_phone: "",
    email: "",
    city: "İstanbul",
    district: "",
    address: "",
    notes: "",
  });

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
      };
      if (q.trim()) params.q = q.trim();
      if (customerType && customerType !== "all") params.customer_type = customerType;
      if (isActive && isActive !== "all") params.is_active = isActive === "true";

      const res = await api.get("/customers", { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, q, customerType, isActive]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== "all") {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    next.set("page", "1");
    setSearchParams(next);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    updateParam("q", q);
  };

  const handleClearFilters = () => {
    setQ("");
    setCustomerType("all");
    setIsActive("true");
    setSearchParams({ page: "1", is_active: "true" });
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    if (!newCust.display_name.trim()) {
      toast.error("Müşteri adı zorunludur.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/customers", newCust);
      toast.success(`Müşteri başarıyla oluşturuldu: ${res.data.customer_number}`);
      setDialogOpen(false);
      setNewCust({
        customer_type: "individual",
        display_name: "",
        company_name: "",
        phone: "",
        alternative_phone: "",
        email: "",
        city: "İstanbul",
        district: "",
        address: "",
        notes: "",
      });
      loadCustomers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#171A1F] tracking-tight">Müşteriler</h1>
          <p className="text-sm text-slate-500 mt-1">
            Kayıtlı cariler, iletişim bilgileri, toplam sözleşmeler ve açık bakiye takibi.
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-[#FF353A] hover:bg-[#E0262B] text-white shrink-0"
            data-testid="btn-new-customer"
          >
            <Plus size={16} className="mr-1.5" />
            Yeni Müşteri
          </Button>
        )}
      </div>

      {/* Filter Bar */}
      <div className="rounded-lg border border-[#DCE2E8] bg-white p-4 shadow-sm">
        <form onSubmit={handleSearchSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Müşteri, firma, tel, no ara..."
              className="pl-9 border-[#DCE2E8]"
              data-testid="customers-search"
            />
          </div>

          <div>
            <Select
              value={customerType}
              onValueChange={(val) => {
                setCustomerType(val);
                updateParam("customer_type", val);
              }}
            >
              <SelectTrigger className="border-[#DCE2E8]" data-testid="filter-customer-type">
                <SelectValue placeholder="Müşteri Türü" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Müşteri Türleri</SelectItem>
                <SelectItem value="individual">Bireysel Müşteri</SelectItem>
                <SelectItem value="corporate">Kurumsal / Firma</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Select
              value={isActive}
              onValueChange={(val) => {
                setIsActive(val);
                updateParam("is_active", val);
              }}
            >
              <SelectTrigger className="border-[#DCE2E8]" data-testid="filter-customer-status">
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="true">Yalnızca Aktif Cariler</SelectItem>
                <SelectItem value="false">Pasif / Birleştirilmiş</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Button type="submit" variant="outline" className="border-[#DCE2E8] text-slate-700">
              Filtrele
            </Button>
            {(q || customerType !== "all" || isActive !== "true") && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleClearFilters}
                className="text-slate-500 hover:text-slate-800 text-xs px-2"
              >
                <X size={14} className="mr-1" />
                Temizle
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Table Card */}
      <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 flex flex-col items-center justify-center text-slate-500">
            <Loader2 size={32} className="animate-spin mb-3 text-[#FF353A]" />
            <p className="text-sm">Müşteriler yükleniyor...</p>
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Müşteri Bulunamadı"
            description="Arama kriterlerinize uygun müşteri kaydı bulunamadı."
            actionLabel={canCreate ? "Yeni Müşteri Ekle" : undefined}
            onAction={canCreate ? () => setDialogOpen(true) : undefined}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="w-28 font-semibold text-slate-700">Müşteri No</TableHead>
                    <TableHead className="font-semibold text-slate-700">Müşteri / Firma Adı</TableHead>
                    <TableHead className="font-semibold text-slate-700">Telefon / Şehir</TableHead>
                    <TableHead className="text-center font-semibold text-slate-700 w-24">İş Sayısı</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Toplam Satış</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Toplam Tahsilat</TableHead>
                    <TableHead className="text-right font-semibold text-slate-700">Kalan Alacak</TableHead>
                    <TableHead className="text-center font-semibold text-slate-700 w-20">Durum</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((cust) => {
                    const balance = parseFloat(cust.outstanding_receivables || 0);
                    return (
                      <TableRow
                        key={cust.id}
                        onClick={() => navigate(`/musteriler/${cust.id}`)}
                        className="cursor-pointer hover:bg-slate-50/80 transition-colors"
                        data-testid={`customer-row-${cust.id}`}
                      >
                        <TableCell className="font-mono text-xs font-semibold text-slate-600">
                          {cust.customer_number}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {cust.customer_type === "corporate" ? (
                              <Building2 size={16} className="text-blue-500 shrink-0" />
                            ) : (
                              <User size={16} className="text-slate-400 shrink-0" />
                            )}
                            <div>
                              <div className="font-semibold text-[#171A1F] text-sm">
                                {cust.display_name}
                              </div>
                              {cust.company_name && cust.company_name !== cust.display_name && (
                                <div className="text-xs text-slate-500 truncate max-w-xs">
                                  {cust.company_name}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs text-slate-700">
                            {cust.phone ? (
                              <span className="flex items-center gap-1 font-mono">
                                <Phone size={12} className="text-slate-400" />
                                {cust.phone}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </div>
                          {(cust.city || cust.district) && (
                            <div className="text-[11px] text-slate-500">
                              {[cust.district, cust.city].filter(Boolean).join(", ")}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center font-semibold text-sm text-slate-700">
                          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                            <Briefcase size={12} className="mr-1 opacity-70" />
                            {cust.jobs_count || 0}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-slate-700">
                          {formatCurrency(cust.total_sales || 0)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-emerald-700 font-medium">
                          {formatCurrency(cust.total_collections || 0)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold">
                          <span className={balance > 0 ? "text-[#FF353A]" : "text-slate-500"}>
                            {formatCurrency(cust.outstanding_receivables || 0)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <StatusBadge
                            tone={cust.is_active ? "success" : "neutral"}
                            label={cust.is_active ? "Aktif" : "Pasif"}
                          />
                        </TableCell>
                        <TableCell className="text-slate-400 pr-4">
                          <ChevronRight size={16} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="p-4 border-t border-[#DCE2E8]">
              <Pagination
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={(p) => {
                  const next = new URLSearchParams(searchParams);
                  next.set("page", String(p));
                  setSearchParams(next);
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* New Customer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Yeni Müşteri Oluştur</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCustomer} className="space-y-3.5 mt-2">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Müşteri Türü</label>
              <Select
                value={newCust.customer_type}
                onValueChange={(v) => setNewCust((prev) => ({ ...prev, customer_type: v }))}
              >
                <SelectTrigger className="border-[#DCE2E8]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Bireysel Müşteri</SelectItem>
                  <SelectItem value="corporate">Kurumsal / Firma</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Müşteri Adı Soyadı *</label>
              <Input
                required
                value={newCust.display_name}
                onChange={(e) => setNewCust((prev) => ({ ...prev, display_name: e.target.value }))}
                placeholder="Örn: Ahmet Yılmaz"
                className="border-[#DCE2E8]"
                data-testid="input-new-cust-name"
              />
            </div>

            {newCust.customer_type === "corporate" && (
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Firma / Şirket Ünvanı</label>
                <Input
                  value={newCust.company_name}
                  onChange={(e) => setNewCust((prev) => ({ ...prev, company_name: e.target.value }))}
                  placeholder="Örn: Güngör Mimarlık Ltd. Şti."
                  className="border-[#DCE2E8]"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Telefon</label>
                <Input
                  value={newCust.phone}
                  onChange={(e) => setNewCust((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="05XX XXX XX XX"
                  className="border-[#DCE2E8]"
                  data-testid="input-new-cust-phone"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">E-Posta</label>
                <Input
                  type="email"
                  value={newCust.email}
                  onChange={(e) => setNewCust((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="ornek@posta.com"
                  className="border-[#DCE2E8]"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Şehir</label>
                <Input
                  value={newCust.city}
                  onChange={(e) => setNewCust((prev) => ({ ...prev, city: e.target.value }))}
                  placeholder="İstanbul"
                  className="border-[#DCE2E8]"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">İlçe</label>
                <Input
                  value={newCust.district}
                  onChange={(e) => setNewCust((prev) => ({ ...prev, district: e.target.value }))}
                  placeholder="Kadıköy"
                  className="border-[#DCE2E8]"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Açık Adres</label>
              <Textarea
                rows={2}
                value={newCust.address}
                onChange={(e) => setNewCust((prev) => ({ ...prev, address: e.target.value }))}
                placeholder="Mahalle, cadde, sokak..."
                className="border-[#DCE2E8]"
              />
            </div>

            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-[#DCE2E8]"
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#FF353A] hover:bg-[#E0262B] text-white"
                data-testid="btn-save-new-cust"
              >
                {submitting ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
                Kaydet
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
